'use client';

import {Link} from '@/i18n/navigation';
import {buttonHasModalSections, RenderableLinkButton} from '@/lib/ensure-link-button';
import {ElementType, ReactNode, useState} from 'react';
import {trackCtaClick} from '@/lib/analytics';
import PopupSectionModal from '@/components/ui/modal/PopupSectionModal';

type ConditionalLinkButtonProps = {
  button: RenderableLinkButton;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ButtonComponent: ElementType<any>;
  buttonType?: string;
  endIcon?: ReactNode;
};

export default function ConditionalLinkButton({ button, ButtonComponent, buttonType = 'cta', endIcon }: ConditionalLinkButtonProps) {
  const [modalOpen, setModalOpen] = useState(false);

  // Modal sections take precedence over any link configuration on the button.
  if (buttonHasModalSections(button)) {
    const handleModalClick = () => {
      trackCtaClick({buttonText: button.label, targetUrl: button.link ?? '', buttonType: 'modal', contentId: button.sys.id});
      setModalOpen(true);
    };

    return (
      <>
        <ButtonComponent
          type='button'
          variant='contained'
          disableElevation
          onClick={handleModalClick}
          endIcon={endIcon}
        >
          {button.label}
        </ButtonComponent>
        <PopupSectionModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          sections={button.modalSectionsCollection!.items}
          ariaLabel={button.label}
        />
      </>
    );
  }

  // Without modal sections a link is required to render anything meaningful.
  if (!button.link) return null;

  const link = button.link;

  const handleClick = () => {
    trackCtaClick({buttonText: button.label, targetUrl: link, buttonType, contentId: button.sys.id});
  };

  if (button.isExternalLink) {
    return (
      <ButtonComponent
        component='a'
        href={link}
        variant='contained'
        target='_blank'
        rel='noopener noreferrer'
        disableElevation
        onClick={handleClick}
        endIcon={endIcon}
      >
        {button.label}
      </ButtonComponent>
    );
  }

  return (
    <ButtonComponent
      component={Link}
      href={button.link}
      variant='contained'
      disableElevation
      onClick={handleClick}
      endIcon={endIcon}
    >
      {button.label}
    </ButtonComponent>
  );
}
